import React from "react";
import { FileX, AlertTriangle, Clock, Upload, CheckCircle2, RotateCcw } from "lucide-react";

export const STATUS = {
  PENDING: "Pending from Client",
  SUBMITTED: "Client Submitted",
  CLOSED: "Accepted & Closed",
  RESUBMIT: "Resubmission Requested",
};
export const ALL_STATUSES = [STATUS.PENDING, STATUS.SUBMITTED, STATUS.CLOSED, STATUS.RESUBMIT];

export const isOverdue = (r) => {
  if (!r || !r.deadline || r.status === STATUS.CLOSED) return false;
  const d = new Date(r.deadline);
  if (isNaN(d)) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return d < today;
};

export const OverdueBadge = ({ className = "" }) => (
  <span data-testid="overdue-badge" className={`inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-bold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 ${className}`}>
    <AlertTriangle className="h-3 w-3" /> Overdue
  </span>
);

// Professional 4-status colour coding (light + dark)
export const statusStyle = {
  [STATUS.PENDING]: "bg-amber-50 text-amber-700 ring-1 ring-amber-200/60 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-500/20",
  [STATUS.SUBMITTED]: "bg-blue-50 text-blue-700 ring-1 ring-blue-200/60 dark:bg-blue-500/15 dark:text-blue-300 dark:ring-blue-500/20",
  [STATUS.CLOSED]: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 dark:bg-emerald-500/15 dark:text-emerald-300 dark:ring-emerald-500/20",
  [STATUS.RESUBMIT]: "bg-rose-50 text-rose-700 ring-1 ring-rose-200/60 dark:bg-rose-500/15 dark:text-rose-300 dark:ring-rose-500/20",
  // legacy fallbacks
  "Pending Upload": "bg-amber-50 text-amber-700",
  "In Progress": "bg-amber-50 text-amber-700",
  Submitted: "bg-blue-50 text-blue-700",
  Closed: "bg-emerald-50 text-emerald-700",
  Rejected: "bg-rose-50 text-rose-700",
};
export const statusIcon = {
  [STATUS.PENDING]: Clock,
  [STATUS.SUBMITTED]: Upload,
  [STATUS.CLOSED]: CheckCircle2,
  [STATUS.RESUBMIT]: RotateCcw,
};

export const StatusBadge = ({ status }) => {
  const Icon = statusIcon[status];
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusStyle[status] || "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}>
      {Icon && <Icon className="h-3 w-3" />}
      {status}
    </span>
  );
};

export const EmptyState = ({ title, subtitle, action }) => (
  <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white py-16 text-center dark:border-slate-700 dark:bg-slate-900">
    <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-50 dark:bg-slate-800">
      <FileX className="h-7 w-7 text-slate-400" />
    </div>
    <h3 className="text-base font-semibold text-[#1a1a2e] dark:text-slate-100">{title}</h3>
    {subtitle && <p className="mt-1 max-w-sm text-sm text-slate-500">{subtitle}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

